import { Component, EventEmitter, Output } from '@angular/core';
import { FCTTitleInsuranceState } from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import { FctGetStatusService } from '../../services/fct-get-status.service';
import { TestHooks } from './in-progress.hooks';

enum StateCategory {
	VALID = 'valid',
	WARNING = 'warning',
	CANCELLED = 'cancelled',
}

@UntilDestroy()
@Component({
	selector: 'app-fct-in-progress',
	templateUrl: './in-progress.component.html',
	standalone: false,
})
export class FCTInProgressComponent {
	testHooks = TestHooks;
	@Output() openModal: EventEmitter<void> = new EventEmitter();
	@Output() cancelOrder: EventEmitter<void> = new EventEmitter();
	public ICON = ICON;
	public gettingStatus = false;
	public states = FCTTitleInsuranceState;
	public state: FCTTitleInsuranceState;
	public stateCategory: StateCategory;
	public stateCategories = StateCategory;

	public message =
		'Your order was successfully submitted and is being processed';
	public buttonMessage: string;
	public buttonIcon: ICON;

	@ViewSelectSnapshot(SelectedMatter.titleInsuranceLastUpdated)
	lastUpdated: number;
	@Select(SelectedMatter.getTitleInsuranceState)
	state$: Observable<FCTTitleInsuranceState>;
	@SelectSnapshot(SelectedMatter.titleInsuranceThirdPartyId)
	thirdPartyId: string;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private fctGetStatusService: FctGetStatusService,
		private snackbarService: SnackbarService,
	) {
		this.state$.pipe(untilDestroyed(this)).subscribe((_state) => {
			this.state = _state;
			this.setMessages(_state);
			this.stateCategory = this.getStateCategory(_state);
		});
	}

	_openModal() {
		this.openModal.emit();
	}

	cancel() {
		this.cancelOrder.emit();
	}

	async _getStatus() {
		this.gettingStatus = true;
		const { matterId, thirdPartyId } = this;
		await this.fctGetStatusService.updateStatus(matterId, thirdPartyId);
		this.gettingStatus = false;
		this.snackbarService.openSnackBar(
			{ text: 'Order status has been updated.', type: 'info' },
			{
				duration: 3000,
				panelClass: 'ml-96',
			},
		);
	}

	setMessages(state: FCTTitleInsuranceState): void {
		switch (state) {
			case FCTTitleInsuranceState.PENDING_CANCELLATION:
				this.message =
					'Your cancellation request is under review. Please check back later';
				this.buttonMessage = 'Check status';
				this.buttonIcon = ICON.REFRESH;
				break;
			case FCTTitleInsuranceState.CANCELLED:
				this.message =
					'To reactivate your order, please submit an update to FCT.';
				this.buttonMessage = 'Amend order';
				this.buttonIcon = ICON.EDIT;
				break;
			case FCTTitleInsuranceState.ON_HOLD:
				this.message =
					'Your order is currently on hold. Contact FCT for further information.';
				this.buttonMessage = 'Amend order';
				this.buttonIcon = ICON.EDIT;
				break;
			case FCTTitleInsuranceState.PENDING_UPDATE:
				this.message =
					'Your order requires attention. Please update to continue.';
				this.buttonMessage = 'Update order';
				this.buttonIcon = ICON.EXTERNAL;
				break;
			case FCTTitleInsuranceState.WAITING_FOR_DOCUMENTS:
				this.message =
					'Please upload the requested document(s) to continue with your order.';
				this.buttonMessage = 'Update order';
				this.buttonIcon = ICON.EXTERNAL;
				break;
			case FCTTitleInsuranceState.IN_UNDERWRITING:
				this.message =
					'Your order has been submitted for underwriting and is awaiting review. Please check back later.';
				this.buttonMessage = 'Check status';
				this.buttonIcon = ICON.REFRESH;
				break;
			case FCTTitleInsuranceState.PENDING_PROCESSING:
				this.message =
					'Your order was successfully submitted and is currently awaiting processing by FCT.';
				this.buttonMessage = 'Check status';
				this.buttonIcon = ICON.REFRESH;
				break;
			default:
				this.message =
					'Your order was successfully submitted and is currently awaiting processing by FCT.';
				this.buttonMessage = 'Amend order';
				this.buttonIcon = ICON.EDIT;
				break;
		}
	}

	getStateCategory(state: FCTTitleInsuranceState): StateCategory {
		switch (state) {
			case FCTTitleInsuranceState.PENDING_CANCELLATION:
			case FCTTitleInsuranceState.CANCELLED:
				return StateCategory.CANCELLED;
			case FCTTitleInsuranceState.ON_HOLD:
			case FCTTitleInsuranceState.PENDING_UPDATE:
			case FCTTitleInsuranceState.WAITING_FOR_DOCUMENTS:
				return StateCategory.WARNING;
			default:
				return StateCategory.VALID;
		}
	}

	async submit(): Promise<void> {
		if (
			this.stateCategory === this.stateCategories.VALID ||
			this.state === FCTTitleInsuranceState.PENDING_CANCELLATION
		) {
			await this._getStatus();

			return;
		}

		this._openModal();
	}
}
